import { z } from "zod/v4";
import { skillLevelSchema } from "../../schemas/shared";
import { fetchApi, paginatedSchema, toSearchParams } from "../internal";
import { skillChangeReasonSchema } from "../profiles";

export const RATING_CHANGES_PAGE_SIZE = 20;
export const ratingSnapshotSchema = z.object({
	anti_deflation_bonus: z.number(),
	games_rated: z.number().int(),
	is_provisional: z.boolean(),
	peak_rating: z.number(),
	rating: z.number(),
	rating_deviation: z.number(),
	volatility: z.number(),
});
const skillStateSchema = z.object({
	// The one-per-account rating reset (migration 345). Optional because audit
	// rows written before it carry no such key.
	rating_reset_used: z.boolean().optional(),
	skill_changes_remaining: z.number().int(),
	skill_level: skillLevelSchema,
	skill_level_confirmed: z.boolean(),
});
export const currentRatingSchema = skillStateSchema.extend({
	rating: ratingSnapshotSchema.nullable(),
	user_id: z.uuid(),
});
export type CurrentRating = z.infer<typeof currentRatingSchema>;
const adjustmentResultSchema = currentRatingSchema.extend({ request_id: z.uuid() });
export const ratingCorrectionSchema = z.object({
	rating: z.number().positive(),
	reason: skillChangeReasonSchema,
	request_id: z.uuid(),
});
export const ratingResetSchema = z.object({
	reason: skillChangeReasonSchema,
	request_id: z.uuid(),
	skill_level: skillLevelSchema,
});
export type RatingCorrection = z.infer<typeof ratingCorrectionSchema>;
export type RatingReset = z.infer<typeof ratingResetSchema>;
/** Games before a player leaves Glicko placement. Mirrors `PLACEMENT_GAMES`. */
export const RATING_PLACEMENT_GAMES = 15;
export const ratingOverviewSchema = z.object({
	leaders: z.array(
		z.object({
			avatar_url: z.string().nullable(),
			display_name: z.string(),
			games_rated: z.number().int(),
			is_provisional: z.boolean(),
			peak_rating: z.number(),
			rank: z.number().int(),
			rating: z.number(),
			rating_deviation: z.number(),
			user_id: z.uuid(),
		}),
	),
	summary: z.object({
		median: z.number(),
		provisional: z.number().int(),
		rated: z.number().int(),
	}),
	tiers: z.array(z.object({ count: z.number().int(), tier: skillLevelSchema })),
});
export type RatingOverview = z.infer<typeof ratingOverviewSchema>;
const ratingChangeSchema = z.object({
	actor_id: z.uuid().nullable(),
	after_state: z.union([ratingSnapshotSchema, skillStateSchema]).nullable(),
	before_state: z.union([ratingSnapshotSchema, skillStateSchema]).nullable(),
	created_at: z.string(),
	id: z.uuid(),
	kind: z.enum([
		"onboarding",
		"skill_change",
		"match",
		"player_reset",
		"admin_reset",
		"admin_correction",
		"admin_skill_correction",
		"system",
	]),
	match_id: z.uuid().nullable(),
	reason: z.string().nullable(),
	request_id: z.uuid().nullable(),
	user_id: z.uuid(),
});
export const ratings = {
	changes: (id: string, offset: number) =>
		fetchApi(
			`/admin/ratings/${id}/changes?${toSearchParams({ limit: RATING_CHANGES_PAGE_SIZE, offset })}`,
			paginatedSchema(ratingChangeSchema),
		),
	correct: (id: string, body: RatingCorrection) =>
		fetchApi(`/admin/ratings/${id}/corrections`, adjustmentResultSchema, {
			body: JSON.stringify(ratingCorrectionSchema.parse(body)),
			method: "POST",
		}),
	get: (id: string) => fetchApi(`/admin/ratings/${id}`, currentRatingSchema),
	overview: () => fetchApi("/admin/ratings", ratingOverviewSchema),
	reset: (id: string, body: RatingReset) =>
		fetchApi(`/admin/ratings/${id}/resets`, adjustmentResultSchema, {
			body: JSON.stringify(ratingResetSchema.parse(body)),
			method: "POST",
		}),
};
